import React, { Fragment } from 'react';
import { Column, Divider, Row, Typography } from '@vp/swan';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { PricingType } from 'src/providers/PriceContextProvider';
import { text } from 'src/utils/localizationUtil';
import {
  getTranslatedNameForProductOptionKey,
  getTranslatedNameForProductOptionValue,
} from 'src/utils/productOptionUtils';
import { PriceDisplay } from './PriceDisplay';

interface CreateRowProps {
  topDivider?: boolean;
  leftColumnElement: JSX.Element;
  rightColumnElement: JSX.Element;
  bottomDivider?: boolean;
}

const CreateRow = ({
  topDivider = false,
  leftColumnElement,
  rightColumnElement,
  bottomDivider = false,
}: CreateRowProps): JSX.Element => {
  return (
    <Row margin={0}>
      <Column span={6} spanXs={6} bgc="standard" pl={0}>
        {topDivider && <Divider my={4} />}
        {leftColumnElement}
        {bottomDivider && <Divider my={4} />}
      </Column>
      <Column span={6} spanXs={6} bgc="standard" pr={0}>
        {topDivider && <Divider my={4} />}
        {rightColumnElement}
        {bottomDivider && <Divider my={4} />}
      </Column>
    </Row>
  );
};

export interface ProductOptionSelectionDisplayProps {
  selectedOptions: Record<string, string>;
  recommendedOptions: Record<string, string>;
  optionsToDisplay: string[];
  isMobile: boolean;
  oldPrice: PricingType;
  newPrice: PricingType;
}

export const ProductOptionSelectionDisplayGrid = ({
  selectedOptions,
  recommendedOptions,
  optionsToDisplay,
  oldPrice,
  newPrice,
}: ProductOptionSelectionDisplayProps): JSX.Element => {
  const { productOptionTranslations } = useProductContext();

  return (
    <>
      <CreateRow
        leftColumnElement={
          <Typography textAlign="left" fontSize={'standard'} marginBottom={'5'} fontWeight="bold">
            {text('product-option-suggestion.original-selection-title')}
          </Typography>
        }
        rightColumnElement={
          <Typography textAlign="left" fontSize={'standard'} marginBottom={'5'} fontWeight="bold">
            {text('product-option-suggestion.suggested-selection-title')}
          </Typography>
        }
      />
      <CreateRow
        leftColumnElement={
          <Typography textAlign="left" fontSize={'xlarge'} marginBottom={'5'} fontWeight="bold">
            {oldPrice && <PriceDisplay price={oldPrice} priceIsLoaded />}
          </Typography>
        }
        rightColumnElement={
          <Typography textAlign="left" fontSize={'xlarge'} marginBottom={'5'} fontWeight="bold">
            {newPrice && <PriceDisplay price={newPrice} priceIsLoaded />}
          </Typography>
        }
        bottomDivider
      />

      {optionsToDisplay.map((optionKey, index) => {
        const showDivider = index > 0;
        return (
          <CreateRow
            key={optionKey}
            leftColumnElement={
              <Fragment>
                <Typography textAlign="left" fontSize={'standard'} marginBottom={'2'}>
                  {getTranslatedNameForProductOptionKey(productOptionTranslations, optionKey)} :
                </Typography>
                <Typography textAlign="left" fontSize={'standard'} fontWeight="bold">
                  {getTranslatedNameForProductOptionValue(
                    productOptionTranslations,
                    optionKey,
                    selectedOptions[optionKey]
                  )}
                </Typography>
              </Fragment>
            }
            rightColumnElement={
              <Fragment>
                <Typography textAlign="left" fontSize={'standard'} marginBottom={'2'}>
                  {getTranslatedNameForProductOptionKey(productOptionTranslations, optionKey)} :
                </Typography>
                <Typography textAlign="left" fontSize={'standard'} fontWeight="bold">
                  {getTranslatedNameForProductOptionValue(
                    productOptionTranslations,
                    optionKey,
                    recommendedOptions[optionKey]
                  )}
                </Typography>
              </Fragment>
            }
            topDivider={showDivider}
          />
        );
      })}
    </>
  );
};
